<template>
  <!-- Manage Board Modal -->
  <BaseModal
    card-width="440px"
    no-padding
    no-toolbar
    no-exit
  >
    <template #default>
      <!-- Modal Content -->
      <div
        class="relative w-full flex flex-col gap-1 p-5 border-b border-border-normal fade-in"
      >
        <!-- Team Logo and Owner Avatar -->
        <div
          class="relative mx-auto"
          style="width: 68px; height: 68px;"
        >
          <!-- Team Logo -->
          <img
            v-if="lens?.avatar"
            :src="lens?.avatar"
            class="team-logo w-9 h-9 rounded-md"
            alt="Team Logo"
          >
          <div
            v-else
            class="team-logo w-9 h-9 flex items-center justify-center p-2 rounded-md bg-neutral-900"
          >
            <BaseText
              type="label"
              size="sm"
              class="text-white"
            >
              {{ lens?.name?.charAt(0) || 'T' }}
            </BaseText>
          </div>
          <!-- Team Owner Avatar -->
          <img
            v-if="inviteOwner?.avatar"
            :src="inviteOwner.avatar"
            class="owner-avatar w-9 h-9 rounded-full"
            alt="Team Owner Avatar"
            referrerpolicy="no-referrer"
          >
          <div
            v-else
            class="owner-avatar w-9 h-9 flex items-center justify-center p-2 rounded-full bg-neutral-50"
          >
            <BaseText
              type="label"
              size="sm"
              class="text-text-muted"
            >
              {{ inviteOwner?.name?.charAt(0) || inviteOwner?.first_name?.charAt(0) || inviteOwner?.last_name?.charAt(0) || 'U' }}
            </BaseText>
          </div>
        </div>
        <!-- Invite Header -->
        <BaseText
          type="label"
          size="md"
          class="text-text-muted text-center"
        >
          You're invited as a guest on {{ lens?.name ? `'${lens.name}'` : 'a lens' }}
        </BaseText>
        <!-- Invite Message -->
        <BaseText
          v-if="inviteOwner"
          type="body"
          size="sm"
          class="text-text-normal text-center"
        >
          {{ inviteOwnerName }} has invited you as a guest to collaborate on analytics.
        </BaseText>
        <!-- Already in a team message -->
        <!-- <BaseText
          v-if="getTeam"
          type="label"
          size="sm"
          class="text-text-muted text-center mt-5 mb-1"
        >
          You're already in a team. You'll need to leave your current team before joining this one.
        </BaseText> -->
        <!-- Close Button -->
        <button
          class="absolute top-3 right-3 p-1 transition-opacity"
          @click="$emit('close')"
        >
          <TagRemoveIcon
            stroke="#5E6678"
            :width="16"
            :height="16"
          />
        </button>
      </div>
      <!-- Modal Actions Footer -->
      <div class="w-full flex justify-between p-5">
        <button
          class="flex justify-center px-2 py-1.5 rounded-md transition duration-100 "
          :class="loadingAction.decline ? 'bg-neutral-25 cursor-default' : 'hover:bg-neutral-25'"
          style="width: 66px;"
          :disabled="loadingAction.decline"
          @click="handleAction(false)"
        >
          <BaseLoadingSpinnerCircle
            v-if="loadingAction.decline"
            class="text-text-disabled"
          />
          <BaseText
            v-else
            type="label"
            size="sm"
            class="text-text-muted"
          >
            Decline
          </BaseText>
        </button>
        <button
          class="submit-btn py-1.5 px-3 rounded-md"
          :disabled="loadingAction.accept"
          @click="handleAction(true)"
        >
          <BaseLoadingSpinnerCircle
            v-if="loadingAction.accept"
            class="text-text-disabled"
          />
          <BaseText
            v-else
            type="label"
            size="sm"
          >
            Accept
          </BaseText>
        </button>
      </div>
    </template>
  </BaseModal>
</template>

<script>
import lensAPI from '@/api/lens'
import FirebaseAPI from '@/api/firebase'
import { mapGetters, mapMutations, mapActions } from 'vuex'

import TagRemoveIcon from '../globals/Icons/TagRemoveIcon.vue'

export default {
  name: 'TeamInvitationModal',
  components: {
    TagRemoveIcon
  },
  data () {
    return {
      team: {},
      lens: {},
      loadingInvitation: true,
      loadingAction: {
        accept: false,
        decline: false
      }
    }
  },
  computed: {
    ...mapGetters('AuthModule', ['getPendingLensInvitations']),
    invitation () {
      return this.getPendingLensInvitations?.[0]
    },
    inviteOwner () {
      return this.invitation?.inviter
    },
    inviteOwnerName () {
      const owner = this.inviteOwner
      if (!owner) return 'A user'
      if (owner.name) return owner.name
      const fullName = [owner.first_name, owner.last_name].filter(Boolean).join(' ')
      return fullName || 'A user'
    }
  },
  async mounted () {
    const { data: lensData } = await lensAPI.Lens.getPublic(this.invitation.lens_id)

    this.lens = lensData
  },
  methods: {
    ...mapActions('LensModule', ['fetchSidebarLenses']),
    ...mapActions('AuthModule', ['setGuestAccess']),
    ...mapMutations('MiscModule', ['SET_SETTINGS_DRAWER_VIEW_MODE']),
    async handleAction (isAccept) {
      this.loadingAction[isAccept ? 'accept' : 'decline'] = true
      try {
        const { data } = await lensAPI.Lens.Invite.handle(this.invitation.id, isAccept)
        // Fetch new guest status and update lenses
        if (isAccept) {
          await this.fetchSidebarLenses()
          await this.setGuestAccess()
        }
        this.$emit('handleAction', isAccept)
      } catch (error) {
        const errMessage = error.response.data.error
        this.$showAlert({ type: 'error', message: errMessage })
        console.error(error)
      } finally {
        this.loadingAction[isAccept ? 'accept' : 'decline'] = false
        this.$emit('close')
      }
    }

  }
}
</script>

<style scoped>
.team-logo {
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
}
.owner-avatar {
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  z-index: 10;
}

@property --submit-btn-opacity-1 {
  syntax: '<number>';
  initial-value: 0.12;
  inherits: false;
}
@property --submit-btn-opacity-2 {
  syntax: '<number>';
  initial-value: 0.12;
  inherits: false;
}
@property --submit-color {
  syntax: '<color>';
  initial-value: #0063F4;
  inherits: false;
}
.submit-btn {
  background: linear-gradient(180deg, rgba(255, 255, 255, var(--submit-btn-opacity-1)) 0%, rgba(255, 255, 255, var(--submit-btn-opacity-2)) 100%), var(--submit-color);
  color: white;
  box-shadow: 0px -1px 12px 0px rgba(255, 255, 255, 0.12) inset, 0px 0px 0px 1px #0063F4;
  transition: box-shadow 100ms ease-in-out,
    color 100ms ease-in-out,
    background-color 100ms ease-in-out,
    --submit-btn-opacity-1 100ms ease-in-out,
    --submit-btn-opacity-2 100ms ease-in-out,
    --submit-color 100ms ease-in-out;
}
.submit-btn:hover {
  --submit-btn-opacity-1: 0.24;
  --submit-btn-opacity-2: 0;
}

.fade-in {
  animation: fadeIn 150ms ease-in-out;
}
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
</style>
